import {
  DELIVERY_TYPE,
  FileResponse,
  MaterialType,
  OrderToSubmitRequest,
  PurchaseRequestItemV2,
  PurchaseRequestV2,
} from "../../types";

/**
 * Pure prefill transforms for the launch-from-PR flow, lifted verbatim from the
 * pre-Carbon OrderByRequest.tsx PR-hydration effect. Kept pure so the RFQ
 * prefill (material EAN/name/quantity/dates/currency/files) can be unit-tested
 * without rendering (T6 ruling: PREFILL PRESERVED).
 */

export const formatDateValue = (value?: string | null) =>
  value ? value.slice(0, 10) : undefined;

/**
 * Map the selected purchase-request items into `ordersToSubmit` entries. The
 * field order and every fallback ladder are preserved exactly — the create
 * payload is derived from these lines.
 */
export const buildPrefillOrders = (
  relevantItems: PurchaseRequestItemV2[],
  requestData: PurchaseRequestV2 | null
): OrderToSubmitRequest[] =>
  relevantItems.map((item): OrderToSubmitRequest => {
    const startDate = formatDateValue(item.neededFrom);
    const endDate = formatDateValue(item.neededTo) || startDate;
    const isPeriod = startDate && endDate && startDate !== endDate;

    return {
      id: item.material?.id || item.id,
      ean: item.material?.ean,
      name: item.material?.globalMaterialName,
      // N5: prefer the solped item's own UM (services capture it directly);
      // fall back to the material's UM for goods.
      measurementUnit: item.measurementUnit ?? item.material?.measurementUnit,
      globalMaterialName: item.material?.globalMaterialName,
      deliveryPlace: item.site ?? item.material?.deliveryPlace ?? null,
      address: item.address ?? undefined,
      fileUrl: item.material?.fileURL,
      internalCode: requestData?.purchaseRequestCode ?? undefined,
      type: item.material?.category?.type ?? MaterialType.ALL,
      company: item.material?.company,
      quantity: item.quantity || 1,
      startSupplyDate: startDate as unknown as Date,
      endSupplyDate: endDate as unknown as Date,
      purpose:
        item.purpose ||
        requestData?.comments ||
        item.request?.comments ||
        undefined,
      brand:
        item.brand ||
        item.description ||
        item.material?.brand ||
        item.material?.details,
      expireDate: false,
      spot: isPeriod ? DELIVERY_TYPE.PERIOD : DELIVERY_TYPE.SPOT,
      files: [],
      // C13 (live path): carry the solped item's per-item attachments so the
      // supplier sees them linked to each item, separate from the general
      // request-level files.
      itemFiles: (item.files ?? []) as unknown as FileResponse[],
      suppliers: [],
      purchaseRequestId: item.purchaseRequestId,
      purchaseRequestItemId: item.id,
      // Seed per-item currency from the solped's per-item budgetCurrency for
      // BOTH goods and services (B6). Items without a per-item budget currency
      // stay undefined and fall back to the global currency selector at submit
      // (mat?.coin || generalBidData?.currency).
      currency: item.budgetCurrency ?? undefined,
      coin: item.budgetCurrency ?? undefined,
    } as unknown as OrderToSubmitRequest;
  });

/** Map the purchase request's request-level files to the general-data file shape. */
export const mapRequestFiles = (requestData: PurchaseRequestV2) =>
  (requestData.files || []).map((file) => ({
    fileName: file.name || "",
    name: file.name || "",
    fileURL: file.fileURL,
    type: file.type,
  }));

/**
 * Whether `prev` already represents exactly this set of items — used to preserve
 * in-progress user edits (suppliers/dates/prices) across intermediate re-renders
 * while still picking up a freshly selected set of items.
 */
export const isSameItemSet = (
  prev: OrderToSubmitRequest[],
  relevantItems: PurchaseRequestItemV2[]
): boolean => {
  if (prev.length === 0) return false;
  const prevItemIds = prev
    .map((p) => p.purchaseRequestItemId)
    .filter(Boolean)
    .sort()
    .join(",");
  const nextItemIds = relevantItems
    .map((i) => i.id)
    .sort()
    .join(",");
  return prevItemIds === nextItemIds;
};
